import { useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { startRecordingAction, stopRecordingAction } from "./RecordingReducer";

const useScreenRecording = (screen = true, audio = true, video = false) => {
  const dispatch = useDispatch();

  // Function to check for screen capture support
  const isScreenCaptureSupported = () => {
    return navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia;
  };

  const isMobileDevice = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  // Only initialize if screen capture is supported and not on mobile
  const canInitializeRecorder = isScreenCaptureSupported() && !isMobileDevice;

  // Conditionally import useReactMediaRecorder
  const { startRecording, stopRecording, mediaBlobUrl, status, error } =
    canInitializeRecorder
      ? require("react-media-recorder").useReactMediaRecorder({
          screen,
          audio,
          video,
          onStop: (blobUrl: any) => {
            dispatchMediaBlobUrl(blobUrl);
            dispatchRecordStatus("stopped");
          },
          onStart: () => {
            dispatchMediaBlobUrl(null);
            dispatchRecordStatus("recording");
          },
        })
      : {};

  const dispatchMediaBlobUrl = useCallback(
    (url) => dispatch({ type: "MEDIA_BLOB_URL", payload: url }),
    [dispatch]
  );

  const dispatchRecordStatus = useCallback(
    (status) => dispatch({ type: "RECORD_STATUS", payload: status }),
    [dispatch]
  );

  useEffect(() => {
    if (status === "idle") {
      dispatchRecordStatus("idle");
    }
  }, [status, dispatchRecordStatus]);

  useEffect(() => {
    if (error) {
      console.error("Screen recording error:", error);
      dispatchRecordStatus("error");
      dispatchMediaBlobUrl(null);
    }
  }, [error, dispatchRecordStatus, dispatchMediaBlobUrl]);

  return {
    startRecording: () => {
      if (!canInitializeRecorder) {
        console.warn("Screen capturing is not supported on this device or browser.");
        dispatchRecordStatus("idle");
        alert("Screen capturing is not supported on mobile devices or this browser.");
        return;
      }
      try {
        dispatch(startRecordingAction());
        startRecording();
      } catch (err) {
        console.error("Failed to start screen recording:", err);
        dispatchRecordStatus("error");
      }
    },
    stopRecording: () => {
      if (!canInitializeRecorder) return;
      try {
        dispatch(stopRecordingAction());
        stopRecording();
      } catch (err) {
        console.error("Failed to stop screen recording:", err);
        dispatchRecordStatus("error");
      }
    },
    mediaBlobUrl: canInitializeRecorder ? mediaBlobUrl : null,
    status: canInitializeRecorder ? status : "idle",
    error: canInitializeRecorder ? error : null,
  };
};

export default useScreenRecording;
